/** @jsxImportSource react */
import { setupPostMessageHandlers } from '@common/helpers/MessageHandlers';
import { getCommunityParams } from '@common/helpers/profile';
import { GwizzyCommunity } from '@common/helpers/transformGwizzyCommunity';
import Portal from '@react/shared/components/Portal';
import {
  OPEN_BOT,
  OPEN_BOT_WITH_MESSAGE_NEXT,
} from '@react/virtual-advisor/constants';
import ChatbotProvider from '@react/virtual-advisor/context/ChatbotProvider';
import { GPTBotMode } from '@sly/core/types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

const Chat = dynamic(() => import('@react/virtual-advisor/components/Chat'), {
  ssr: false,
});

export default function ChatWrapperNext({
  gwizzyCommunity,
  chatbotMode,
}: {
  gwizzyCommunity: GwizzyCommunity | null;
  chatbotMode: GPTBotMode;
}) {
  const { query, push, pathname, asPath } = useRouter();

  const params = query?.communitySlug ? getCommunityParams(query) : query;

  const onApplySearchFilters = (query: { [key: string]: string | number }) => {
    push(
      {
        pathname,
        query,
      },
      undefined,
      { shallow: true, scroll: false }
    );
  };

  const onPush = (url: string) => {
    push(url, undefined, { shallow: true, scroll: false });
  };

  return (
    <Portal>
      <ChatbotProvider
        query={params}
        gwizzyCommunity={gwizzyCommunity}
        onApplySearchFilters={onApplySearchFilters}
        onPush={onPush}
        chatbotMode={chatbotMode}
        pathname={asPath}
      >
        <Chat />
      </ChatbotProvider>
    </Portal>
  );
}

export function ChatWrapperWithDealy({
  gwizzyCommunity,
  waitFor,
  chatbotMode,
}: {
  gwizzyCommunity: GwizzyCommunity | null;
  waitFor: number;
  chatbotMode: GPTBotMode;
}) {
  const [isShown, setIsShown] = useState(false);

  const handleEvent = useCallback(
    async ({ event, payload }: { event: string; payload: any }) => {
      switch (event) {
        case OPEN_BOT_WITH_MESSAGE_NEXT: {
          setIsShown(true);
          window.postMessage(
            {
              event: OPEN_BOT,
              payload,
            },
            '*'
          );
          break;
        }
        default:
          break;
      }
    },
    []
  );

  const postMessageHandlers = useCallback(
    async (msg: any) => {
      const { event, payload } = msg.data;
      handleEvent({
        event,
        payload,
      });
    },
    [handleEvent]
  );

  useEffect(() => {
    const unsubscribePostMessage = setupPostMessageHandlers(
      window,
      postMessageHandlers
    );
    return () => {
      unsubscribePostMessage();
    };
  }, [postMessageHandlers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsShown(true);
    }, waitFor);
    return () => clearTimeout(timer);
  }, [waitFor]);

  if (!isShown) {
    return null;
  }

  return (
    <ChatWrapperNext
      chatbotMode={chatbotMode}
      gwizzyCommunity={gwizzyCommunity}
    />
  );
}
